import { useState, useEffect, useCallback } from 'react';
import { perplexityService } from '@services/api/perplexity.service';
import { NewsArticle } from '@/types/perplexity.types';
import { supabase } from '@services/supabase/supabaseClient';

interface UseMarketNewsReturn {
  news: NewsArticle[];
  isLoading: boolean;
  error: string | null;
  refreshNews: () => Promise<void>;
  lastUpdated: Date | null;
}

// Cache settings
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (matches cron job frequency)
const LOADING_MESSAGE = 'Getting fresh vibes for you...';

// In-memory session cache (separate from database cache)
let sessionCache: { data: NewsArticle[]; timestamp: number } | null = null;

export const useMarketNews = (): UseMarketNewsReturn => {
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchNews = useCallback(async (bypassCache: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Step 1: Check session cache first (fastest)
      if (!bypassCache && sessionCache) {
        const now = Date.now();
        if (now - sessionCache.timestamp < CACHE_DURATION) {
          console.log('📰 Using session cached news');
          setNews(sessionCache.data);
          setLastUpdated(new Date(sessionCache.timestamp));
          setIsLoading(false);
          return;
        }
      }

      // Step 2: Try database cache (pre-fetched by cron)
      if (!bypassCache) {
        console.log('📰 Checking database cache...');
        const { data: dbCache, error: dbError } = await supabase
          .from('market_news_cache')
          .select('articles, fetched_at')
          .order('fetched_at', { ascending: false })
          .limit(1)
          .single();

        if (!dbError && dbCache) {
          const fetchedAt = new Date(dbCache.fetched_at).getTime();
          const now = Date.now();
          const isFresh = now - fetchedAt < CACHE_DURATION;

          if (isFresh) {
            console.log(
              '✅ Using fresh database cache (age:',
              Math.round((now - fetchedAt) / (60 * 60 * 1000)),
              'hours)'
            );
            const articles = dbCache.articles as NewsArticle[];

            // Update session cache
            sessionCache = {
              data: articles,
              timestamp: fetchedAt,
            };

            setNews(articles);
            setLastUpdated(new Date(fetchedAt));
            setIsLoading(false);
            return;
          } else {
            console.log(
              '⚠️  Database cache is stale (age:',
              Math.round((now - fetchedAt) / (60 * 60 * 1000)),
              'hours)'
            );
          }
        }
      }

      // Step 3: Fallback to direct Perplexity API call
      console.log(`📰 ${LOADING_MESSAGE}`);
      const result = await perplexityService.getMarketNewsStructured('day');

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data) {
        const now = Date.now();

        // Update session cache only (not database - cron handles that)
        sessionCache = {
          data: result.data,
          timestamp: now,
        };

        setNews(result.data);
        setLastUpdated(new Date(now));
        console.log('✅ Market news fetched from API:', result.data.length, 'articles');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch market news';
      setError(errorMessage);
      console.error('useMarketNews error:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchNews(false); // Use cache on initial load
  }, [fetchNews]);

  // Refresh function (bypasses all caches and forces API call)
  const refreshNews = useCallback(async () => {
    await fetchNews(true); // bypassCache = true
  }, [fetchNews]);

  return {
    news,
    isLoading,
    error,
    refreshNews,
    lastUpdated,
  };
};
